import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle, Phone, Sun, Zap, DollarSign, Leaf, TrendingDown, Shield } from 'lucide-react';

const Solar = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Solar Panel Installation & Services
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Professional solar panel installation and electrical integration services in London, Kingston, and surrounding areas. Reduce your energy costs and carbon footprint with clean, renewable solar energy.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold">
                <Link to="/contact">Get Free Solar Quote</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900">
                <a href="tel:519-709-9242">
                  <Phone className="mr-2 h-5 w-5" />
                  Call 519-709-9242
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Solar */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Switch to Solar Energy
              </h2>
              <p className="text-lg text-gray-600 mb-4">
                Solar energy is one of the fastest-growing renewable energy sources in Ontario. With government incentives and decreasing equipment costs, there's never been a better time to invest in solar power for your home or business.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Studerus Electric Inc. provides complete solar panel installation services, from initial consultation and system design to electrical integration and grid connection.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Reduce Energy Bills</h3>
                    <p className="text-gray-600">Lower or eliminate your monthly electricity costs</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Environmental Impact</h3>
                    <p className="text-gray-600">Reduce your carbon footprint and help the environment</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Increase Property Value</h3>
                    <p className="text-gray-600">Solar installations add value to your property</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Government Incentives</h3>
                    <p className="text-gray-600">Take advantage of available rebates and programs</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_next-day-electric/artifacts/vpiuguos_1000013527.jpg"
                alt="Solar panel installation"
                className="rounded-lg shadow-xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
            Benefits of Solar Energy
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card>
              <CardHeader>
                <DollarSign className="h-12 w-12 text-green-600 mb-4" />
                <CardTitle>Cost Savings</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Significantly reduce or eliminate your monthly electricity bills with solar power generation.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Leaf className="h-12 w-12 text-green-600 mb-4" />
                <CardTitle>Eco-Friendly</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Clean, renewable energy that reduces your carbon footprint and helps fight climate change.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <TrendingDown className="h-12 w-12 text-blue-600 mb-4" />
                <CardTitle>Energy Independence</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Reduce reliance on the grid and protect yourself from rising electricity rates.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Shield className="h-12 w-12 text-blue-900 mb-4" />
                <CardTitle>Long-Term Investment</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Solar panels typically last 25-30 years, providing decades of clean energy and savings.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Solar Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
            Our Solar Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card>
              <CardHeader>
                <Sun className="h-12 w-12 text-yellow-500 mb-4" />
                <CardTitle>Solar Consultation & Design</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Site assessment and analysis</li>
                  <li>• Energy usage evaluation</li>
                  <li>• Custom system design</li>
                  <li>• ROI calculations</li>
                  <li>• Free quotes</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Zap className="h-12 w-12 text-blue-600 mb-4" />
                <CardTitle>Solar Panel Installation</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Professional roof mounting</li>
                  <li>• Ground-mounted systems</li>
                  <li>• Quality panel installation</li>
                  <li>• Structural assessments</li>
                  <li>• Warranty support</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Zap className="h-12 w-12 text-purple-600 mb-4" />
                <CardTitle>Electrical Integration</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Inverter installation</li>
                  <li>• Electrical panel upgrades</li>
                  <li>• Grid connection setup</li>
                  <li>• Net metering configuration</li>
                  <li>• ESA compliance</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Shield className="h-12 w-12 text-green-600 mb-4" />
                <CardTitle>Battery Storage Solutions</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Energy storage systems</li>
                  <li>• Battery backup installation</li>
                  <li>• Off-grid capabilities</li>
                  <li>• Emergency power backup</li>
                  <li>• System monitoring</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <DollarSign className="h-12 w-12 text-orange-600 mb-4" />
                <CardTitle>Incentive Assistance</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Rebate program guidance</li>
                  <li>• Application support</li>
                  <li>• Documentation assistance</li>
                  <li>• Federal incentives</li>
                  <li>• Provincial programs</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Sun className="h-12 w-12 text-red-600 mb-4" />
                <CardTitle>Maintenance & Monitoring</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• System monitoring</li>
                  <li>• Performance optimization</li>
                  <li>• Cleaning services</li>
                  <li>• Troubleshooting & repairs</li>
                  <li>• Annual inspections</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Installation Process */}
      <section className="py-16 bg-blue-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
            Solar Installation Process
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            From consultation to activation, we handle every step of your solar installation
          </p>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="bg-blue-900 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="font-semibold text-lg mb-2">Consultation</h3>
              <p className="text-gray-600 text-sm">Free site assessment and energy analysis</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-900 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="font-semibold text-lg mb-2">Design</h3>
              <p className="text-gray-600 text-sm">Custom system design and proposal</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-900 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="font-semibold text-lg mb-2">Permits</h3>
              <p className="text-gray-600 text-sm">Obtain necessary permits and approvals</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-900 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                4
              </div>
              <h3 className="font-semibold text-lg mb-2">Installation</h3>
              <p className="text-gray-600 text-sm">Professional solar panel installation</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-900 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                5
              </div>
              <h3 className="font-semibold text-lg mb-2">Activation</h3>
              <p className="text-gray-600 text-sm">ESA inspection and grid connection</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
            Common Solar Questions
          </h2>
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>How much can I save with solar panels?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Savings vary based on your energy usage, system size, and electricity rates. Many homeowners see 50-100% reduction in their electricity bills. We provide detailed ROI calculations during consultation.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>How long do solar panels last?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Quality solar panels typically last 25-30 years or more. Most manufacturers offer 25-year performance warranties, and panels continue producing energy beyond their warranty period.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Do solar panels work in Ontario winters?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Yes! Solar panels actually work more efficiently in cold weather. While snow can temporarily reduce output, panels are angled to shed snow quickly, and annual production remains excellent in Ontario.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>What incentives are available in Ontario?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Ontario offers various federal and provincial incentives for solar installations. We'll help you navigate available rebates, tax credits, and financing options to maximize your savings.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-900 to-blue-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Go Solar?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Contact Studerus Electric Inc. today for a free solar consultation and find out how much you can save
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button asChild size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold">
              <Link to="/contact">Get Free Solar Quote</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900">
              <a href="tel:519-709-9242">
                <Phone className="mr-2 h-5 w-5" />
                Call 519-709-9242
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Solar;
